import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "./MenuBar";
import Footer from "./Footer";
import "./Style.css";
import PageTabs from "./shared/PageTabs";
import CollapsiblePanel from "./shared/CollapsiblePanel";

const env = require("../EnvironmentVariables.json");

const Tabs = {
  OPEN: "open",
  REFUNDED: "refunded",
  REJECTED: "rejected",
};

const TabLabels = {
  [Tabs.OPEN]: "Unresolved",
  [Tabs.REFUNDED]: "Refunded",
  [Tabs.REJECTED]: "Rejected",
};

const ROADMAP_TABS = [
  { id: "insights", label: "Insights", enabled: false, comingSoon: true },
  { id: "automation", label: "Automation", enabled: false, comingSoon: true },
  { id: "integrations", label: "Integrations", enabled: false, comingSoon: true },
];

const DisputesPage = () => {
  const scId = localStorage.getItem("SC_Id");
  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const [disputes, setDisputes] = useState([]);
  const navigate = useNavigate();

  // Load the last active tab from localStorage, default to OPEN if not found
  const [currentTab, setCurrentTab] = useState(() => {
    const savedTab = localStorage.getItem("disputesLastActiveTab");
    return savedTab && Object.values(Tabs).includes(savedTab) ? savedTab : Tabs.OPEN;
  });

  const [searchByCustomer, setSearchByCustomer] = useState("");
  const [searchByInvoice, setSearchByInvoice] = useState("");
  const [sortField, setSortField] = useState("dispute_id");
  const [sortOrder, setSortOrder] = useState("desc");
  const pageTabs = useMemo(
    () => [
      { id: Tabs.OPEN, label: `${TabLabels[Tabs.OPEN]} Disputes`, enabled: true },
      { id: Tabs.REFUNDED, label: `${TabLabels[Tabs.REFUNDED]} Disputes`, enabled: true },
      { id: Tabs.REJECTED, label: `${TabLabels[Tabs.REJECTED]} Disputes`, enabled: true },
      ...ROADMAP_TABS,
    ],
    []
  );

  useEffect(() => {
    const fetchDisputes = async () => {
      if (!scId) {
        return;
      }

      try {
        const response = await fetch(
          `${env.serverId}/SCO/getDisputes.php?sc_id=${scId}&status=${currentTab}`
        );
        const data = await response.json();

        if (data.status === "Success") {
          setDisputes(data.disputes || []);
          setCurrentPage(1);
        } else {
          setDisputes([]);
        }
      } catch (error) {
        console.error("Failed to fetch disputes", error);
        setDisputes([]);
      }
    };

    fetchDisputes();
  }, [currentTab, scId]);

  const handleTabClick = (tab) => {
    setCurrentPage(1);
    setCurrentTab(tab);
    localStorage.setItem("disputesLastActiveTab", tab);
  };

  const handleCustomer = (customerName) => {
    setSearchByCustomer(customerName);
    setCurrentPage(1);
  };

  const handleInvoice = (invoiceId) => {
    setSearchByInvoice(invoiceId);
    setCurrentPage(1);
  };

  const handleSortFieldChange = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const filteredDisputes = useMemo(() => {
    const customerQuery = (searchByCustomer || "").toLowerCase();
    const invoiceQuery = (searchByInvoice || "").toLowerCase();

    const normalizeString = (value) => (value ? value.toString().toLowerCase() : "");

    const getSortValue = (dispute) => {
      switch (sortField) {
        case "customer_name":
          return normalizeString(dispute.customer_name);
        case "invoice_id":
          return Number(dispute.invoice_id) || 0;
        case "requested_amount":
          return Number(dispute.requested_amount) || 0;
        case "task_id":
          return Number(dispute.task_id) || 0;
        case "dispute_id":
        default:
          return Number(dispute.dispute_id) || 0;
      }
    };

    return disputes
      .filter((dispute) => normalizeString(dispute.customer_name).includes(customerQuery))
      .filter((dispute) => {
        if (!invoiceQuery) {
          return true;
        }
        const invoice = normalizeString(dispute.invoice_id);
        return invoice.includes(invoiceQuery);
      })
      .sort((a, b) => {
        const aValue = getSortValue(a);
        const bValue = getSortValue(b);

        if (aValue === bValue) {
          return 0;
        }

        if (aValue === null || aValue === undefined || aValue === "") {
          return 1;
        }

        if (bValue === null || bValue === undefined || bValue === "") {
          return -1;
        }

        let comparison = 0;

        if (typeof aValue === "string" && typeof bValue === "string") {
          comparison = aValue.localeCompare(bValue);
        } else {
          comparison = aValue > bValue ? 1 : -1;
        }

        return sortOrder === "asc" ? comparison : -comparison;
      });
  }, [disputes, searchByCustomer, searchByInvoice, sortField, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredDisputes.length / itemsPerPage));

  // Adjust currentPage if it exceeds totalPages
  const safePage = Math.min(currentPage, totalPages);

  const handleLeftPagination = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleRightPagination = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const renderActionButtons = (dispute) => {
    if (currentTab === Tabs.OPEN) {
      return (
        <button
          className="edit-view-cls"
          onClick={() => navigate(`/disputes/${dispute.dispute_id}`, { state: dispute })}
        >
          View Detail
        </button>
      );
    }
    return "--";
  };

  return (
    <>
      <MenuBar />
      <PageTabs
        tabs={pageTabs}
        currentTab={currentTab}
        onChange={handleTabClick}
        ariaLabel="Disputes status tabs"
      />

      <CollapsiblePanel id="disputes-toolbar" title="Disputes controls" className="sco-toolbar-collapsible">
        <div className="tasks-search-section sco-toolbar">
          <div className="tasks-search-inputs">
            <div className="page-input-block">
              <label className="task-label">Customer&apos;s Name</label>
              <input
                type="text"
                placeholder="Search by Customer's Name"
                className="search-input"
                value={searchByCustomer}
                onChange={(e) => handleCustomer(e.target.value)}
              />
            </div>
            <div className="page-input-block">
              <label className="task-label">Invoice ID</label>
              <input
                type="text"
                placeholder="Search by Invoice ID"
                className="search-input"
                value={searchByInvoice}
                onChange={(e) => handleInvoice(e.target.value)}
              />
            </div>
          </div>
        </div>
      </CollapsiblePanel>

      <div className="tasks-container">
        <table className="tasks-table">
          <thead>
            <tr>
              <th onClick={() => handleSortFieldChange("dispute_id")} className="sortable-header">
                Dispute ID {sortField === "dispute_id" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSortFieldChange("invoice_id")} className="sortable-header">
                Invoice ID {sortField === "invoice_id" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSortFieldChange("customer_name")} className="sortable-header">
                Customer {sortField === "customer_name" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Status</th>
              <th onClick={() => handleSortFieldChange("requested_amount")} className="sortable-header">
                Requested Amount {sortField === "requested_amount" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSortFieldChange("task_id")} className="sortable-header">
                Task ID {sortField === "task_id" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredDisputes.length !== 0 ? (
              filteredDisputes
                .slice((safePage - 1) * itemsPerPage, (safePage - 1) * itemsPerPage + itemsPerPage)
                .map((dispute) => (
                  <tr key={dispute.dispute_id}>
                    <td>{dispute.dispute_id}</td>
                    <td>{dispute.invoice_id}</td>
                    <td>{dispute.customer_name}</td>
                    <td>{dispute.invoice_status}</td>
                    <td>{dispute.requested_amount != null ? `$${dispute.requested_amount}` : "N/A"}</td>
                    <td>{dispute.task_id}</td>
                    <td>{renderActionButtons(dispute)}</td>
                  </tr>
                ))
            ) : (
              <></>
            )}
          </tbody>
        </table>

        {filteredDisputes.length === 0 && (
          <p style={{ textAlign: "center", fontSize: 24, marginTop: 30 }}>
            {currentTab === Tabs.OPEN && "You're all caught up"}
            {currentTab === Tabs.REFUNDED && "No Refunded Disputes"}
            {currentTab === Tabs.REJECTED && "No Rejected Disputes"}
          </p>
        )}

        {/* Pagination at the bottom */}
        {filteredDisputes.length > 0 && (
          <div className="pagination pagination-compact" style={{ justifyContent: "center", marginTop: "20px" }}>
            <button
              className="page-item"
              disabled={safePage === 1}
              onClick={handleLeftPagination}
            >
              {"<"}
            </button>
            <span className="pagination-status">
              Page {safePage} / {totalPages}
            </span>
            <button
              className="page-item"
              disabled={safePage >= totalPages || filteredDisputes.length === 0}
              onClick={handleRightPagination}
            >
              {">"}
            </button>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default DisputesPage;
